import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Calendar, IndianRupee, Star, ChevronDown, ChevronUp, Bed, DoorOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Separator } from '../ui/separator';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useUserContext } from '../../app/protectedRoute/UserContext';

interface ItemDetail {
  type: "bed" | "room" | "property";
  property_id?: string;
  property_name?: string;
  property_address?: string;
  count?: number;
}

interface Booking {
  id: string;
  guest_name: string;
  guest_phone_number: string;
  host_phone_number: string;
  checkin: string;
  checkout: string;
  item_details: ItemDetail[];
  total_price: string;
  status: 'ongoing' | 'completed' | 'cancelled' | 'upcoming';
  booking_id: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  upcoming:  { label: 'Upcoming',  className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  ongoing:   { label: 'Ongoing',   className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  completed: { label: 'Completed', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' },
};

const formatDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const BookingCard: React.FC<{ booking: Booking }> = ({ booking }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { state } = useUserContext();
  const token = Cookies.get('token');

  const propertyDetail = booking.item_details.find(d => d.type === 'property');
  const bedDetail = booking.item_details.find(d => d.type === 'bed');
  const roomDetail = booking.item_details.find(d => d.type === 'room');
  const statusCfg = STATUS_CONFIG[booking.status] ?? STATUS_CONFIG.upcoming;

  const handleReviewSubmit = async () => {
    if (!propertyDetail?.property_id || !state.hostID) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/api/hosts/property-reviews/`,
        { property: propertyDetail.property_id, rating, comment, guest_id: state.hostID },
        { headers: { Authorization: `Token ${token}` } }
      );
      setReviewOpen(false);
      setRating(0);
      setComment('');
    } catch {
      alert('Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        {/* Top colour bar by status */}
        <div className={`h-1 w-full ${booking.status === 'ongoing' ? 'bg-green-500' : booking.status === 'upcoming' ? 'bg-primary' : booking.status === 'cancelled' ? 'bg-red-500' : 'bg-border'}`} />

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              {propertyDetail?.property_id ? (
                <Link to={`/property/${propertyDetail.property_id}`} className="hover:underline">
                  <h3 className="font-semibold text-foreground text-base truncate">
                    {propertyDetail.property_name ?? 'Property'}
                  </h3>
                </Link>
              ) : (
                <h3 className="font-semibold text-foreground text-base truncate">
                  {propertyDetail?.property_name ?? 'Property'}
                </h3>
              )}
              {propertyDetail?.property_address && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                  <MapPin size={11} className="shrink-0" />
                  {propertyDetail.property_address}
                </p>
              )}
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${statusCfg.className}`}>
              {statusCfg.label}
            </span>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-secondary/40 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar size={10} /> Check-in
              </p>
              <p className="text-sm font-semibold text-foreground">{formatDate(booking.checkin)}</p>
            </div>
            <div className="bg-secondary/40 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar size={10} /> Check-out
              </p>
              <p className="text-sm font-semibold text-foreground">{formatDate(booking.checkout)}</p>
            </div>
          </div>

          {/* Inventory + price row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {(bedDetail?.count ?? 0) > 0 && (
                <span className="flex items-center gap-1">
                  <Bed size={13} /> {bedDetail!.count} Bed{bedDetail!.count! > 1 ? 's' : ''}
                </span>
              )}
              {(roomDetail?.count ?? 0) > 0 && (
                <span className="flex items-center gap-1">
                  <DoorOpen size={13} /> {roomDetail!.count} Room{roomDetail!.count! > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5 font-bold text-foreground text-base">
              <IndianRupee size={14} />
              {Number(booking.total_price).toLocaleString('en-IN')}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" className="flex-1 rounded-xl" onClick={() => setDetailsOpen(true)}>
              View Details
            </Button>
            {booking.status === 'completed' && (
              <Button size="sm" className="flex-1 rounded-xl bg-primary hover:bg-primary/90" onClick={() => setReviewOpen(true)}>
                <Star size={14} className="mr-1" /> Rate
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <Row label="Booking ID" value={booking.booking_id} mono />
            <Separator />
            <Row label="Property" value={propertyDetail?.property_name} />
            <Row label="Address" value={propertyDetail?.property_address} />
            <Separator />
            <Row label="Check-in" value={formatDate(booking.checkin)} />
            <Row label="Check-out" value={formatDate(booking.checkout)} />
            <Separator />
            <Row label="Host Phone" value={booking.host_phone_number || '—'} />
            <Row label="Pay at Property" value={`₹${Number(booking.total_price).toLocaleString('en-IN')}`} bold />
            <Row label="Status" value={statusCfg.label} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rate Your Stay</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Your rating</p>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(n)}
                  >
                    <Star
                      size={28}
                      className={n <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Comment</p>
              <textarea
                className="w-full border border-border rounded-xl p-3 text-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                rows={4}
                placeholder="Share your experience..."
                value={comment}
                onChange={e => setComment(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setReviewOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={handleReviewSubmit} disabled={submitting || rating === 0}>
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Row = ({ label, value, mono = false, bold = false }: { label: string; value?: string; mono?: boolean; bold?: boolean }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className={`text-right text-foreground ${mono ? 'font-mono text-xs break-all' : ''} ${bold ? 'font-semibold' : ''}`}>
      {value || '—'}
    </span>
  </div>
);

export default BookingCard;
